import { athleteDisplayName } from '@strength/domain';
import {
  WorkoutChangeType,
  zDateChangeType,
  zExerciseChangeType,
  zFilterLogs,
} from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { RouterOutput } from '../../../../trpc';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { trpc } from '../../../../trpc/react';
import { DataTableWithContext } from '../../../components/table/data-table';
import {
  DataTableFilter,
  FilterDataType,
  FilterOption,
} from '../../../components/table/data-table-context';
import { useViewer } from '../../../context/viewer';
import { ActivityLogDrawer } from './activity-log-drawer';
import { getLocale } from '../../../utils/i18n';

export type Logs = RouterOutput['activityLog']['logs']['records'][0];
export type Athletes = RouterOutput['roster']['teamAthletes'][0];

export const getChangeTypeMap = (
  t: I18nTranslator,
): Record<WorkoutChangeType, string> => ({
  exercise_added: t('exerciseAdded'),
  exercise_removed: t('exerciseRemoved'),
  exercise_updated: t('exerciseUpdated'),
  exercise_prescription_change: t('exercisePrescriptionChange'),
  assigned_date_changed: t('assignedDateChanged'),
  tablet_exercise_added: t('tabletExerciseAdded'),
  tablet_exercise_updated: t('tabletExerciseUpdated'),
  tablet_exercise_removed: t('tabletExerciseRemoved'),
  tablet_prescription_change: t('tabletPrescriptionChange'),
});

export function ActivityLogPage() {
  const { t } = useTranslation();
  const {
    team: { selectedTeam: teamId },
  } = useViewer();
  const [queryFilters, setQueryFilters] =
    useState<z.infer<typeof zFilterLogs>>();
  const [athletes, setAthletes] = useState<Athletes[]>([]);
  const [selectedLog, setSelectedLog] = useState<Logs | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const { data, isLoading } = trpc.activityLog.logs.useQuery({
    athletes: queryFilters?.athletes,
    changeTypes: queryFilters?.changeTypes,
    startDate: queryFilters?.startDate
      ? new Date(queryFilters.startDate).toISOString()
      : undefined,
    endDate: queryFilters?.endDate
      ? new Date(queryFilters.endDate).toISOString()
      : undefined,
  });
  trpc.roster.teamAthletes.useQuery({}, {
      onSuccess: (data) => {
        setAthletes(data);
      },
    },
  );

  const columnHelper = createColumnHelper<Logs>();
  const CHANGE_TYPE_MAP = getChangeTypeMap(t);

  const columns = [
    columnHelper.accessor('athlete', {
      cell: ({ row }) => (
        <div>
          {row.original?.athlete
            ? athleteDisplayName(row.original.athlete)
            : 'N/A'}
        </div>
      ),
      header: () => <div>{t('athlete')}</div>,
    }),
    columnHelper.accessor('createdAt', {
      header: () => <div>{t('dateAndTime')}</div>,
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        const date = createdAt ? new Date(createdAt) : new Date();
        return (
          <div>
            {date.toLocaleString(getLocale(), {
              hour: 'numeric',
              minute: 'numeric',
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            })}
          </div>
        );
      },
    }),
    columnHelper.accessor('changeType', {
      header: () => <div>{t('changeType')}</div>,
      cell: ({ row }) => <div>{CHANGE_TYPE_MAP[row.original.changeType]}</div>,
    }),
  ];

  const athletesFilters: DataTableFilter<Athletes> = {
    id: 'athletes',
    title: t('athletes'),
    type: 'select' as const,
    options: athletes.map<FilterOption<Athletes>>((athlete) => ({
      id: athlete.id,
      label: athleteDisplayName(athlete),
      value: athlete,
    })),
    multiselect: true,
    showSearch: true,
    showSelectAll: true,
  };

  const workoutChangeTypeOptions = [
    ...zExerciseChangeType.options,
    ...zDateChangeType.options,
  ];

  const changeTypeFilter = {
    id: 'changeType',
    title: t('changeType'),
    type: 'select' as const,
    options: workoutChangeTypeOptions.map<FilterOption<WorkoutChangeType>>(
      (changeType) => ({
        id: changeType,
        label: CHANGE_TYPE_MAP[changeType],
        value: changeType,
      }),
    ),
  };

  const dateRangeFilter = {
    id: 'dateRange',
    type: 'dateRange' as const,
  };

  const filters = [athletesFilters, changeTypeFilter, dateRangeFilter];

  const handleFilterChange = (filters: Record<string, FilterDataType>) => {
    setQueryFilters({
      athletes:
        filters['athletes']?.map((athlete: Athletes) => athlete.id) || [],
      changeTypes: filters['changeType'],
      ...(filters['dateRange']
        ? {
            startDate: filters['dateRange']['value']['start'],
            endDate: filters['dateRange']['value']['end'],
          }
        : {}),
    });
  };

  const logData = data?.records || [];
  const totalCount = data?.totalCount;

  return (
    <div className="p-6 grow overflow-hidden h-full flex">
      <DataTableWithContext
        title={t('activity')}
        columns={columns}
        data={logData}
        loading={isLoading}
        enableFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        enablePagination
        rowCount={totalCount}
        initialPageIndex={0}
        initialPageSize={50}
        onRowClick={(item) => {
          setSelectedLog(item);
          setOpen(true);
        }}
      />
      <ActivityLogDrawer
        log={selectedLog}
        open={open}
        onClose={() => {
          setSelectedLog(null);
          setOpen(false);
        }}
      />
    </div>
  );
}
